/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import { useEffect } from "react";
import dynamic from "next/dynamic";

import {
  AssetType,
  DataProvider,
  getPlayable,
  IConfigurationModel,
  IMediaModel,
  MediaStreamType,
  MediaType,
  RouteHelper,
  useConfigurationSelector,
  useGetMedia,
  useSessionTokenSelector,
} from "@nf/common";
import {
  useMediaDetailsActionsSelector,
  useMediaDetailsMediaSelector,
} from "screens/MediaDetailsScreen";
import { WatchSEO } from "components";

import styles from "./Watch.module.scss";
import { googleAnalyticsEvent } from "../../lib/gtm";
import {
  useHideCookieConsentButton,
  useMediaParentalRatingGuard,
  useRouter,
} from "../../hooks";

const VideoPlayer = dynamic(() => import("components/VideoPlayer"), {
  ssr: false,
});

export type IWatchScreenRouteProps = {
  id?: string;
  TRIAL?: string;
};

export interface WatchProps {
  configuration: IConfigurationModel;
}

export const Watch = (props: WatchProps) => {
  const router = useRouter();
  const { id } = router.query as IWatchScreenRouteProps;
  const playTrial = MediaStreamType.Trial in router.query;
  const media = useMediaDetailsMediaSelector();
  const { isLoading } = useMediaDetailsActionsSelector();
  const sessionToken = useSessionTokenSelector();
  const configuration = useConfigurationSelector();

  useHideCookieConsentButton();

  useMediaParentalRatingGuard(media);

  const getMedia = useGetMedia();

  useEffect(() => {
    media && media.MediaTypeCode && googleAnalyticsEvent.trackMovieEnter(media);
  }, [media]);

  useEffect(() => {
    if (id && media?.Id !== id && !isLoading) {
      getMedia({
        MediaId: id,
      });
    }
  }, [id, isLoading, media]);

  const compareByOrderDescending = (a: IMediaModel, b: IMediaModel) => {
    if (a.OrderInParent && b.OrderInParent) {
      return b.OrderInParent - a.OrderInParent;
    } else {
      return 0;
    }
  };

  const goToChildMedia = async (parentMedia: IMediaModel) => {
    if (media && media.MediaTypeCode === MediaType.Series) {
      const episode = await DataProvider.getLastViewEpisode(media?.Id);

      router.replace(RouteHelper.goToPlayer(episode));
    } else if (parentMedia.Media) {
      const sortedMedia = parentMedia.Media.filter(getPlayable).sort(
        compareByOrderDescending
      );
      const firstMedia = sortedMedia[0];

      if (firstMedia) {
        router.replace(RouteHelper.goToPlayer(firstMedia));
      }
    }
  };

  if (
    media &&
    media.MediaTypeCode !== AssetType.Video &&
    media.MediaTypeCode !== AssetType.Channel &&
    media.MediaTypeCode !== AssetType.Episode
  ) {
    // If it isn't VOD, probably it'sSERIES or SEASON - then play nested media.
    goToChildMedia(media);
    return null;
  }

  if (isLoading) {
    return null;
  }

  if (!media) return null;

  return (
    <>
      <WatchSEO />
      <div className={styles.container}>
        <VideoPlayer
          config={props?.configuration}
          chromecastId={configuration?.System?.ChromecastAppId}
          token={sessionToken}
          media={media}
          playTrial={playTrial}
        />
      </div>
    </>
  );
};

export default Watch;
